import React, { useState, useEffect } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

export const DashboardLayout: React.FC = () => {
  const { user, role, logout } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sidebar_collapsed') === 'true'
    }
    return false
  })

  // Dark Mode state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark'
    }
    return false
  })

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [isDarkMode])

  const toggleTheme = () => {
    const nextDark = !isDarkMode
    setIsDarkMode(nextDark)
    localStorage.setItem('theme', nextDark ? 'dark' : 'light')
  }

  // Google OAuth states
  const [isGoogleConnected, setIsGoogleConnected] = useState(false)
  const [isGisLoaded, setIsGisLoaded] = useState(false)
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

  useEffect(() => {
    // Dynamically load Google Identity Services Client
    if (!document.getElementById('google-gis-script')) {
      const script = document.createElement('script')
      script.id = 'google-gis-script'
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = () => setIsGisLoaded(true)
      document.body.appendChild(script)
    } else {
      setIsGisLoaded(true)
    }

    const syncGoogleToken = () => {
      const savedToken = localStorage.getItem('google_drive_access_token')
      const savedExpiry = localStorage.getItem('google_drive_token_expiry')
      if (savedToken && savedExpiry) {
        const exp = Number(savedExpiry)
        if (exp > Date.now()) {
          setIsGoogleConnected(true)
          return
        }
      }
      setIsGoogleConnected(false)
    }

    syncGoogleToken()
    window.addEventListener('google-token-updated', syncGoogleToken)
    return () => window.removeEventListener('google-token-updated', syncGoogleToken)
  }, [])

  const handleConnectGoogle = () => {
    if (!googleClientId) {
      alert('⚠️ ไม่พบ Google Client ID ในระบบคอนฟิกกรุณาตรวจสอบไฟล์ .env.local')
      return
    }

    if (!(window as any).google?.accounts?.oauth2) {
      alert('⌛ กำลังโหลดระบบยืนยันสิทธิ์ของ Google กรุณารอสักครู่แล้วลองอีกครั้ง')
      return
    }

    try {
      const client = (window as any).google.accounts.oauth2.initTokenClient({
        client_id: googleClientId,
        // Request scopes for Drive, Sheets, Calendar, and Gmail Send
        scope: 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/gmail.send',
        callback: (response: any) => {
          if (response.error) {
            alert(`เชื่อมต่อ Google Drive ล้มเหลว: ${response.error_description || response.error}`)
            return
          }
          if (response.access_token) {
            const exp = Date.now() + Number(response.expires_in) * 1000
            localStorage.setItem('google_drive_access_token', response.access_token)
            localStorage.setItem('google_drive_token_expiry', String(exp))
            setIsGoogleConnected(true)
            // Notify other tabs/components
            window.dispatchEvent(new Event('google-token-updated'))
            alert('🔌 เชื่อมต่อบัญชี Google สำเร็จแล้ว! พร้อมใช้งานทุกฟีเจอร์')
          }
        }
      })
      client.requestAccessToken({ prompt: 'consent' })
    } catch (err: any) {
      alert(`เกิดข้อผิดพลาดในการเชื่อมต่อ: ${err.message}`)
    }
  }

  // State for tracking the currently selected left-rail category
  const [activeCategory, setActiveCategory] = useState<string>(() => {
    const path = window.location.pathname
    if (path.startsWith('/admin')) return 'admin'
    if (path.startsWith('/department')) return 'hod'
    if (path.startsWith('/teacher')) return 'teaching'
    return 'teaching'
  })

  // Automatically sync left-rail category with route path changes
  useEffect(() => {
    const path = location.pathname
    if (role === 'admin') {
      if (path.startsWith('/admin')) {
        setActiveCategory('admin')
      } else if (path.startsWith('/teacher')) {
        setActiveCategory('teaching')
      }
    } else if (role === 'head_of_department') {
      if (path.startsWith('/department')) {
        setActiveCategory('hod')
      } else if (path.startsWith('/teacher')) {
        setActiveCategory('teaching')
      }
    } else if (role === 'teacher') {
      if (path.startsWith('/teacher/profile')) {
        setActiveCategory('account')
      } else {
        setActiveCategory('teaching')
      }
    }
  }, [location.pathname, role])

  // Detect mobile vs desktop view reactive
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1024
    }
    return false
  })

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Resolve role metadata
  const getRoleLabel = () => {
    if (role === 'admin') return 'ผู้ดูแลระบบ (Admin)'
    if (role === 'head_of_department') return 'หัวหน้ากลุ่มสาระ (HoD)'
    return 'ครูผู้สอน (Teacher)'
  }

  const getProfileName = () => {
    return user ? `${user.first_name} ${user.last_name}` : 'กำลังโหลด...'
  }

  const renderNavLink = (to: string, icon: string, text: string, end = false) => {
    return (
      <NavLink 
        to={to} 
        end={end} 
        className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '10px 14px',
          fontSize: '14px',
          borderRadius: '8px',
          transition: 'all 0.2s',
        }}
      >
        <span style={{ fontSize: '18px' }}>{icon}</span>
        <span>{text}</span>
      </NavLink>
    )
  }

  const sidebarWidth = isMobile ? '260px' : (isCollapsed ? '75px' : '260px')

  return (
    <div style={{ display: 'flex', minHeight: '100vh', position: 'relative' }}>
      {/* Background Orbs */}
      <div className="glow-orb glow-orb-primary" style={{ opacity: 0.04 }} />
      <div className="glow-orb glow-orb-secondary" style={{ opacity: 0.04 }} />

      {/* Sidebar Overlay for Mobile */}
      {isMobile && sidebarOpen && (
        <div 
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 19,
          }}
        />
      )}

      {/* Double Column Sidebar */}
      <aside style={{
        width: sidebarWidth,
        background: 'var(--bg-card)',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'row',
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: 20,
        transition: 'all var(--transition-normal)',
        transform: isMobile ? (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
        overflow: 'hidden'
      }}>
        
        {/* COLUMN 1: Slim Left Icon Bar */}
        <div style={{
          width: '75px',
          background: 'var(--bg-base)',
          borderRight: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '24px 4px',
          gap: '16px',
          height: '100%',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          {/* Logo & Category Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', width: '100%' }}>
            {/* Logo */}
            <div style={{ 
              fontWeight: '800', 
              color: 'var(--primary)', 
              fontSize: '18px', 
              marginBottom: '10px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              SSX
            </div>

            {/* Dynamic Categories based on Role */}
            {role === 'admin' && (
              <>
                <button 
                  type="button"
                  onClick={() => setActiveCategory('admin')}
                  title="ระบบแอดมิน"
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    border: 'none',
                    background: activeCategory === 'admin' ? 'var(--primary)' : 'transparent',
                    color: activeCategory === 'admin' ? '#ffffff' : 'var(--text-secondary)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    gap: '4px',
                    padding: '6px 2px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>🛡️</span>
                  <span style={{ fontSize: '9px', fontWeight: '700', whiteSpace: 'nowrap' }}>ระบบ</span>
                </button>

                <button 
                  type="button"
                  onClick={() => setActiveCategory('hod')}
                  title="ดูข้อมูลกลุ่มสาระ"
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    border: 'none',
                    background: activeCategory === 'hod' ? 'var(--primary)' : 'transparent',
                    color: activeCategory === 'hod' ? '#ffffff' : 'var(--text-secondary)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    gap: '4px',
                    padding: '6px 2px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>📊</span>
                  <span style={{ fontSize: '9px', fontWeight: '700', whiteSpace: 'nowrap' }}>กลุ่มสาระ</span>
                </button>

                <button 
                  type="button"
                  onClick={() => setActiveCategory('teaching')}
                  title="งานสอนส่วนตัว"
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    border: 'none',
                    background: activeCategory === 'teaching' ? 'var(--primary)' : 'transparent',
                    color: activeCategory === 'teaching' ? '#ffffff' : 'var(--text-secondary)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    gap: '4px',
                    padding: '6px 2px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>📚</span>
                  <span style={{ fontSize: '9px', fontWeight: '700', whiteSpace: 'nowrap' }}>งานสอน</span>
                </button>
              </>
            )}

            {role === 'head_of_department' && (
              <>
                <button 
                  type="button"
                  onClick={() => setActiveCategory('hod')}
                  title="ระบบ HoD"
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    border: 'none',
                    background: activeCategory === 'hod' ? 'var(--primary)' : 'transparent',
                    color: activeCategory === 'hod' ? '#ffffff' : 'var(--text-secondary)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    gap: '4px',
                    padding: '6px 2px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>📊</span>
                  <span style={{ fontSize: '9px', fontWeight: '700', whiteSpace: 'nowrap' }}> HoD </span>
                </button>

                <button 
                  type="button"
                  onClick={() => setActiveCategory('teaching')}
                  title="งานสอนส่วนตัว"
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    border: 'none',
                    background: activeCategory === 'teaching' ? 'var(--primary)' : 'transparent',
                    color: activeCategory === 'teaching' ? '#ffffff' : 'var(--text-secondary)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    gap: '4px',
                    padding: '6px 2px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>📚</span>
                  <span style={{ fontSize: '9px', fontWeight: '700', whiteSpace: 'nowrap' }}>งานสอน</span>
                </button>
              </>
            )}

            {role === 'teacher' && (
              <>
                <button 
                  type="button"
                  onClick={() => setActiveCategory('teaching')}
                  title="งานสอนส่วนตัว"
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    border: 'none',
                    background: activeCategory === 'teaching' ? 'var(--primary)' : 'transparent',
                    color: activeCategory === 'teaching' ? '#ffffff' : 'var(--text-secondary)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    gap: '4px',
                    padding: '6px 2px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>🍎</span>
                  <span style={{ fontSize: '9px', fontWeight: '700', whiteSpace: 'nowrap' }}>งานสอน</span>
                </button>

                <button 
                  type="button"
                  onClick={() => setActiveCategory('account')}
                  title="บัญชีผู้ใช้งาน"
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    border: 'none',
                    background: activeCategory === 'account' ? 'var(--primary)' : 'transparent',
                    color: activeCategory === 'account' ? '#ffffff' : 'var(--text-secondary)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    gap: '4px',
                    padding: '6px 2px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>👤</span>
                  <span style={{ fontSize: '9px', fontWeight: '700', whiteSpace: 'nowrap' }}>บัญชี</span>
                </button>
              </>
            )}
          </div>

          {/* Slim Column Bottom Actions (Collapsed Profile & Logout) */}
          {(isCollapsed && !isMobile) && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', width: '100%' }}>
              <div 
                style={{ fontSize: '18px', cursor: 'pointer' }}
                title={getProfileName()}
              >
                👤
              </div>
              <button 
                type="button"
                onClick={logout} 
                style={{ 
                  background: 'transparent',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="ออกจากระบบ"
              >
                🚪
              </button>
            </div>
          )}
        </div>

        {/* COLUMN 2: Submenu Panel */}
        {(!isCollapsed || isMobile) && (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }}>
            {/* Header of Column 2 */}
            <div style={{ 
              padding: '24px 16px', 
              borderBottom: '1px solid var(--border)', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexShrink: 0
            }}>
              <span style={{ 
                fontSize: '15px', 
                fontWeight: '700', 
                color: 'var(--text-primary)',
                whiteSpace: 'nowrap'
              }}>
                {activeCategory === 'admin' ? '🛡️ แผงจัดการระบบ' : 
                 activeCategory === 'hod' ? '📊 กลุ่มสาระ HoD' :
                 activeCategory === 'teaching' ? '📚 งานสอนสะสม' : '👤 บัญชีส่วนตัว'}
              </span>
              {isMobile && (
                <button 
                  onClick={() => setSidebarOpen(false)}
                  style={{ 
                    background: 'transparent', 
                    border: 'none', 
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Submenu links */}
            <nav style={{ flex: 1, padding: '20px 10px', display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto' }}>
              {role === 'admin' && activeCategory === 'admin' && (
                <>
                  {renderNavLink('/admin', '🏠', 'แผงควบคุมระบบ', true)}
                  {renderNavLink('/admin/sessions', '⏱️', 'รอบบันทึกคะแนน (Sessions)')}
                  {renderNavLink('/admin/schedule-status', '📅', 'สถานะส่งตามตารางสอน')}
                  {renderNavLink('/admin/teachers', '👨‍🏫', 'การจัดการข้อมูลครู')}
                  {renderNavLink('/admin/academic', '🏫', 'จัดการข้อมูลวิชาการ')}
                  {renderNavLink('/admin/profile', '👤', 'ข้อมูลส่วนตัวแอดมิน')}
                </>
              )}

              {role === 'admin' && activeCategory === 'teaching' && (
                <>
                  {renderNavLink('/teacher', '🏠', 'แผงวิเคราะห์การสอน', true)}
                  {renderNavLink('/teacher/entry', '✏️', 'ป้อนคะแนนสอบ')}
                  {renderNavLink('/teacher/subjects', '📐', 'คะแนนเต็มรายวิชา')}
                  {renderNavLink('/teacher/classrooms', '🏫', 'จัดการตารางสอน')}
                  {renderNavLink('/teacher/classrooms-list', '📚', 'ห้องเรียนทั้งหมด')}
                  {renderNavLink('/teacher/exam-repository', '📂', 'คลังข้อสอบ')}
                  {renderNavLink('/teacher/calendar', '📅', 'ปฏิทินส่งคะแนน')}
                </>
              )}

              {(role === 'head_of_department' || role === 'admin') && activeCategory === 'hod' && (
                <>
                  {renderNavLink('/department', '📊', 'แดชบอร์ดติดตาม', true)}
                  {renderNavLink('/department/classrooms', '🏫', 'สถานะการส่งรายห้อง')}
                  {renderNavLink('/department/reports', '📈', 'รายงานผลสัมฤทธิ์')}
                  {role === 'head_of_department' && renderNavLink('/department/profile', '👤', 'ข้อมูลส่วนตัวหัวหน้า')}
                </>
              )}

              {role === 'head_of_department' && activeCategory === 'teaching' && (
                <>
                  {renderNavLink('/teacher/entry', '✏️', 'ป้อนคะแนนสอบ')}
                  {renderNavLink('/teacher/subjects', '📐', 'คะแนนเต็มรายวิชา')}
                  {renderNavLink('/teacher/classrooms', '🏫', 'จัดการตารางสอน')}
                  {renderNavLink('/teacher/classrooms-list', '📚', 'ห้องเรียนทั้งหมด')}
                  {renderNavLink('/teacher/exam-repository', '📂', 'คลังข้อสอบ')}
                  {renderNavLink('/teacher/calendar', '📅', 'ปฏิทินส่งคะแนน')}
                </>
              )}

              {role === 'teacher' && activeCategory === 'teaching' && (
                <>
                  {renderNavLink('/teacher', '🏠', 'แผงวิเคราะห์การสอน', true)}
                  {renderNavLink('/teacher/entry', '✏️', 'ป้อนคะแนนสอบ')}
                  {renderNavLink('/teacher/subjects', '📐', 'คะแนนเต็มรายวิชา')}
                  {renderNavLink('/teacher/classrooms', '🏫', 'จัดการตารางสอน')}
                  {renderNavLink('/teacher/classrooms-list', '📚', 'ห้องเรียนทั้งหมด')}
                  {renderNavLink('/teacher/exam-repository', '📂', 'คลังข้อสอบ')}
                  {renderNavLink('/teacher/calendar', '📅', 'ปฏิทินส่งคะแนน')}
                </>
              )}

              {role === 'teacher' && activeCategory === 'account' && (
                <>
                  {renderNavLink('/teacher/profile', '👤', 'จัดการข้อมูลส่วนตัว')}
                </>
              )}
            </nav>

            {/* Profile & Logout at the bottom of Column 2 */}
            <div style={{ padding: '20px 12px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '13px', 
                fontWeight: '600', 
                marginBottom: '10px', 
                whiteSpace: 'nowrap', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
              }} title={getProfileName()}>
                👤 {getProfileName()}
              </div>
              <button 
                onClick={logout} 
                className="btn btn-outline" 
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  fontSize: '13px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                🚪 ออกจากระบบ
              </button>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        transition: 'padding var(--transition-normal)',
        paddingLeft: isMobile ? '0' : sidebarWidth,
        minWidth: 0,
        overflowX: 'hidden'
      }}>
        
        {/* Header Bar */}
        <header style={{
          height: isMobile ? '56px' : '70px',
          background: 'var(--bg-card)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid var(--border)',
          padding: isMobile ? '0 12px' : '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {isMobile ? (
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="btn-hamburger"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-primary)',
                  fontSize: '24px',
                  cursor: 'pointer',
                }}
              >
                ☰
              </button>
            ) : (
              <button 
                onClick={() => {
                  const val = !isCollapsed
                  setIsCollapsed(val)
                  localStorage.setItem('sidebar_collapsed', String(val))
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-primary)',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'background 0.2s',
                }}
                title={isCollapsed ? "ขยายเมนู" : "ย่อเมนู"}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {isCollapsed ? '»' : '«'}
              </button>
            )}
            {!isMobile && (
              <div className="header-academic-year" style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                🏫 ภาคเรียนปัจจุบันของระบบ
              </div>
            )}
          </div>

          {/* Header Controls (Theme Toggle & Google Account integration) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            
            {/* Theme Toggle Button (Dark mode toggle) */}
            <button
              onClick={toggleTheme}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '6px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-primary)'
              }}
              title={isDarkMode ? 'สลับเป็นโหมดสว่าง' : 'สลับเป็นโหมดมืด'}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>

            {/* Central Google Connection Button */}
            {isGoogleConnected ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ 
                  fontSize: '11px', 
                  fontWeight: '700', 
                  color: 'var(--success)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '5px',
                  background: 'rgba(16,185,129,0.1)',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  border: '1px solid rgba(16,185,129,0.2)'
                }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--success)', display: 'inline-block' }} />
                  ✓ เชื่อมต่อ Google แล้ว
                </span>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleConnectGoogle}
                disabled={!isGisLoaded}
                style={{
                  background: 'linear-gradient(135deg, #4285F4, #34A853)',
                  color: '#fff',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: isGisLoaded ? 'pointer' : 'not-allowed',
                  opacity: isGisLoaded ? 1 : 0.6
                }}
              >
                🔑 เชื่อมบัญชี Google
              </button>
            )}

            <span className="badge-role" style={{
              background: 'rgba(0, 184, 148, 0.15)',
              border: '1px solid var(--primary)',
              color: 'var(--text-primary)',
              padding: isMobile ? '3px 8px' : '4px 10px',
              borderRadius: '20px',
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: '600',
              maxWidth: isMobile ? '120px' : 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {isMobile ? (role === 'admin' ? 'Admin' : role === 'head_of_department' ? 'HoD' : 'Teacher') : getRoleLabel()}
            </span>
          </div>
        </header>

        {/* Content Wrapper */}
        <main className="main-page-padding" style={{ flex: 1, background: 'var(--bg-base)', minWidth: 0, width: '100%', overflowX: 'hidden' }}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default DashboardLayout
